<template>
  <div>
    <div class="orders">
      <zero-state
        v-if="showZeroState"
        :meta="zeroStateMeta"
        :background-transparent="false"
        @open-create-order-modal="openNewOrderModal"
      />
      <Container v-else class="orders__container">
        <OrdersTopBar
          :page="currentPage"
          class="orders__top-bar"
          :sortValue="sortValue"
          @is-empty-filters="isEmptyFilters = $event"
          @changePage="changePage"
        />
        <div class="orders__list">
            <table-header class="orders__headerTable" @sort="sortTable" :list="headerList" :key="lang" />
            <template v-if="!isLoading">
                <template v-if="hasOrders">
                    <PreviewOrder
                        v-for="(order, key) in orders"
                        :key="key"
                        :name="'order'"
                        class="orders__order"
                        :item="order"
                        :status-enum="statusesEnum"
                        :order-enum="$options.orderEnum"
                        :mobile-status-text-color="mobileStatusTextColor(order.status, $options.orderEnum)"
                        @openOrder="openOrder"
                    />
                </template>
                <div v-else class="orders__table-empty">
                    {{ $t('orphan.empty') }}
                </div>
            </template>
        </div>
          <div v-if="!isLoading" class="orders__pagination">
              <pagination
                :size="'small'"
                :total="totalOrdersCount"
                :page-size="ordersCountOnPage"
                :current="currentPage"
                @page-change="changePage"
              />
          </div>
          <preloader v-if="isLoading || !isInit"/>
      </Container>
      <NewOrderModal @updateOrders="loadOrders"/>
        <transition name="slide-fade">
            <OrderInfo v-if="infoState" @click="openOrder" :status-enum="statusesEnum" />
        </transition>
    </div>

  </div>
</template>

<script>

import {mapState, mapActions, mapGetters} from 'vuex';

import OrdersTopBar from "/public/components/elements/orders/OrdersTopBar";
import OrdersListHeader from "/public/components/elements/orders/OrdersListHeader";
import PreviewOrder from "/public/components/elements/orders/PreviewOrder";
import AtPagination from "@/public/components/common/uiKit/Pagination.vue";
import EmptyTable from "/public/components/elements/EmptyTable";
import NewOrderModal from "/public/components/modals/orders/NewOrderModal";
import Preloader from "/public/components/common/uiKit/spinner/index.vue";
import Pagination from "@/public/components/common/uiKit/Pagination.vue";
import {ORDER_STATUS_ENUM} from "../../common/constants/orders-status-enum";

import CommonInfoModal from "/public/components/modals/commonInfo";
import Container from "@/public/components/common/uiKit/Container.vue";
import TableHeader from "@/public/components/common/uiKit/table/TableHeader.vue";
import OrderInfo from "@/public/components/elements/orders/OrdersInfo.vue";
import InvoicesTopBar from "@/public/components/elements/bills/InvoicesTopBar.vue";
import { isAlbertCrypto } from "@/config";

export default {
    name: 'OrdersPage',
    orderEnum: ORDER_STATUS_ENUM,
    title: 'orders.title',
    rulesAlias: 'orders',
    components: {
      InvoicesTopBar,
        OrderInfo,
        TableHeader,
        Container,
        Preloader,
        OrdersTopBar,
        OrdersListHeader,
        PreviewOrder,
        AtPagination,
        EmptyTable,
        NewOrderModal,
        Pagination,
        CommonInfoModal,
        ZeroState: () => import(/* webpackChunkName: "public/pages/zeroState" */ '/public/pages/zeroState')
    },
    data () {
        return {
            page: 1,
            sortValue: null,
            currentPage: 1,
            isEmptyFilters: false
        }
    },
    computed: {
        ...mapState('app', [
            'isInit',
            'isLoading',
        ]),
        ...mapState('orders', [
            'ordersCountOnPage',
            'orders',
            'totalOrdersCount',
            'infoState'
        ]),
        ...mapState('user', ['lang']),
        ...mapGetters('organizations', ['userRoleRules']),
        isCreateOrderAllowed () {
          return this.userRoleRules['orders'].entities.create_order
        },
        showZeroState({ isEmptyFilters, hasOrders }) {
          return isEmptyFilters && !hasOrders
        },
        headerList () {
            return [
                {
                    title: 'id',
                    slug: 'id',
                    isSort: false
                },
                {
                    title: this.$t('orders.date_of_creation'),
                    slug: 'date',
                    isSort: true
                },
                {
                    title: this.$t('orders.order-number'),
                    slug: 'number',
                    isSort: false
                },
                {
                    title: this.$t('orders.currency'),
                    slug: 'amount',
                    isSort: false
                },
                {
                    title: this.$t('orders.network'),
                    slug: 'invoice_currency',
                    isSort: false
                },
                {
                    title: this.$t('orders.amount'),
                    slug: 'payment_amount',
                    isSort: false
                },
                {
                    title: this.$t('orders.received_amount'),
                    slug: 'rate',
                    isSort: false
                },
                {
                    title: this.$t('orders.status'),
                    slug: 'status',
                    isSort: false
                },

            ]
        },
        statusesEnum() {
            const statuses = {
                [ORDER_STATUS_ENUM.INIT]: {
                    title: 'orders.init',
                    colorTag: 'init',
                },
                [ORDER_STATUS_ENUM.PENDING]: {
                    title: 'orders.pending',
                    colorTag: 'pending',
                },
                [ORDER_STATUS_ENUM.PROCESSED]: {
                    title: 'orders.processed',
                    colorTag: 'processed',
                },
                [ORDER_STATUS_ENUM.EXPIRED]: {
                    title: 'orders.expired',
                    colorTag: 'expired',
                },
                [ORDER_STATUS_ENUM.ERROR]: {
                    title: 'orders.error',
                    colorTag: 'error',
                },
                [ORDER_STATUS_ENUM.REFUND]: {
                    title: 'orders.refund',
                    colorTag: 'error',
                },
                [ORDER_STATUS_ENUM.PARTIAL]: {
                    title: 'orders.partial',
                    colorTag: 'partial',
                },
                [ORDER_STATUS_ENUM.OVERPAID]: {
                    title: 'orders.overpaid',
                    colorTag: 'overpaid',
                },
            };
            return statuses;
        },
        hasOrders() {
            return Boolean(this.orders.length);
        },
        zeroStateMeta({ isCreateOrderAllowed }) {
          const meta = {
            title: this.$t('zero-states.orders.title'),
            subtitle: this.$t('zero-states.orders.subtitle'),
            actions: [
              {
                title: this.$t('zero-states.orders.actions.api-integration.title'),
                info: this.$t('zero-states.orders.actions.api-integration.info'),
                btnName: this.$t('zero-states.orders.actions.api-integration.btn-name'),
                btnSecondary: true,
                withoutIcon: true,
                apiMethod: '/api-reference/05orders/post_make-order'
              }
            ],
            type: 'default'
          }

          if (isCreateOrderAllowed) {
            meta.actions.unshift({
              title: this.$t('zero-states.orders.actions.create-order.title'),
              info: this.$t('zero-states.orders.actions.create-order.info'),
              btnName: this.$t('zero-states.orders.actions.create-order.btn-name'),
              action: 'open-create-order-modal'
            })
          }

          return meta
        }
    },
    mounted() {
        this.loadOrders()
    },
    async created() {
        const isAllowed = this.userRoleRules[this.$options.rulesAlias].isAllowed;
        const isShowAllowed = this.userRoleRules[this.$options.rulesAlias].entities.view_orders
        if (!isAllowed || !isShowAllowed) {
          this.$Message.error(this.$t('rules.not_allowed'));
          if ( isAlbertCrypto ) {
            await this.$router.push('/merchant/business-wallet');
          } else {
            await this.$router.push('/merchant/payments');
          }
        }
    },
    methods: {
        ...mapActions('orders', [
            'fetchOrders',
            'fetchOrder',
        ]),
        ...mapActions('app', [
          'openModal',
        ]),
        openNewOrderModal() {
          if (!this.isCreateOrderAllowed) {
            return
          }
          this.openModal({
            name: 'NewOrderModal',
          });
        },
        loadOrders () {
            const {
                $route,
                isInit,
                fetchOrders,
            } = this;

            if (isInit) {
                fetchOrders({
                    route: $route,
                    showUrlFilters: false,
                    page: this.currentPage
                });
            }
        },
        async openOrder (orderId) {
            const {
                fetchOrder,
            } = this;

            await fetchOrder({
                id: orderId,
            })
                .then(order => {
                    this.order = order;
                });
        },
        sortTable (slug, direction) {
            this.sortValue = direction ? "ASC" : "DESC"
        },
        mobileStatusTextColor(status, orderEnum) {
            switch (status) {
                case orderEnum.INIT:
                    return '#6190e8';
                case orderEnum.PENDING:
                    return '#6190e8';
                case orderEnum.PROCESSED:
                    return '#13ce66';
                case orderEnum.ERROR:
                    return '#EB5757';
                case orderEnum.REFUND:
                    return '#ffc82c';
                case orderEnum.EXPIRED:
                    return '#8F949A';
                case orderEnum.PARTIAL:
                    return '#ffc82c';
                default:
                    return '#8F949A';
            }
        },
        changePage(pageNumber) {
          this.currentPage = pageNumber
        },
    },
    watch: {
        isInit() {
            const {
                $route,
                fetchOrders,
            } = this;

            fetchOrders({
                route: $route,
                showUrlFilters: false,
                page: this.currentPage
            });
        },
    },
};

</script>

<style lang="scss" scoped>
.orders {
  padding: 0 30px 30px 30px;
  font-family: $Gilroy;

  &__order {
    background: var(--new-front-main-bg);
  }
  &__table-empty {
    font-weight: 500;
    padding: 30px;
    font-size: 24px;
    text-align: center;
    margin-top: 20px;
    color: var(--new-front-text-primary-bg-modal-primary);
  }
  &__list {
    overflow-x: auto;
  }

  &__title {
    display: flex;
    align-items: center;
    font-weight: 400;
    font-size: $h1;
    color: var(--new-front-text-primary-bg-modal-primary);
    margin-left: $p5;
    margin-bottom: $p30;
    line-height: 31px;
    > svg {
      margin-left: 5px;
    }
  }

  &__paragraph-block {
    display: flex;
    align-items: start;
  }

  &__paragraph {
    font-family: $Gilroy;
    font-style: normal;
    font-weight: 500;
    font-size: var(--font-size-h3);
    line-height: var(--line-height);
    color: var(--new-front-primary-label-color);
  }
}

@media screen and (max-width: 1300px) {
  .orders {
    &__top-bar {
      padding-left: 20px;
      padding-right: 20px;
    }
  }
}

@media screen and (max-width: 1180px) {
  .orders {

    &__title {
      padding-left: 10px;
      padding-right: 10px;
    }

    &__paragraph {


      padding-left: 10px;
      padding-right: 10px;

    }
  }


  @media screen and (max-width: 375px) {
    .orders {

      &__top-bar {
        padding-right: 10px;
        padding-left: 10px;
      }
    }

  }
}
@media (max-width: 950px) {
  .orders {
    padding: $p10;
    &__headerTable {
      display: none;
    }
    &__title {
      font-size: $h3;
      margin-bottom: 10px;
      margin-left: 10px;
    }
  }
}
::v-deep {
  .header {
    grid-template-columns: 20% 20% 15% 25% 25% 15% 15% 15%;
    @media screen and (max-width: 1600px) {
      grid-template-columns: 20% 20% 20% 26% 35% 15% 15% 15%;
    }
    padding: 0;
    gap: 0;
    &__item {
      background: var(--new-front-select-item-bg-active);
      min-height: 56px;
      &:first-child {
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
        padding-left: 40px;
      }
      &:last-of-type {
        border-top-right-radius: 10px;
        border-bottom-right-radius: 10px;
      }
    }
  }
}
.slide-fade-enter-active {
  transition: all .3s ease;
}
.slide-fade-leave-active {
  transition: all .3s ease;
}
.slide-fade-enter, .slide-fade-leave-to
  /* .slide-fade-leave-active до версии 2.1.8 */ {
  transform: translateX(10px);
  opacity: 0;
}
</style>
